import { FC } from 'react'

import styled from 'styled-components'
import { Control, Controller } from 'react-hook-form'
import { ComboboxItem, OptionsFilter } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

import { Select } from 'src/Components/Select'
import {
  Client,
  AddClientBody,
  useAddClientMutation,
} from 'src/Redux/api/clientsApi.ts'
import { Input } from 'src/Components/Input/index.tsx'
import { ToggleSwitch } from 'src/Components/ToggleSwitch'
import { HintTooltip } from 'src/Components/HintTooltip'
import { getSeasons } from 'src/Helpers/functions.tsx'
import { AddClientModal } from 'src/Pages/Clients/AddClientsModal.tsx'
import {
  fromVatSwitches,
  OfferFormData,
  toVatSwitches,
  VatSwitches,
} from 'src/Pages/Calculator/helpers.ts'
import { colors, device } from 'src/Helpers/constants.ts'
import { DatePicker } from 'src/Components/DatePicker/index.tsx'
import { formatClientName } from 'src/Helpers/organizationalForm'

const CalcParamsWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
  flex-direction: column;
`

const ParamsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  width: 100%;
`

const ParamsBlockLeft = styled(ParamsBlock)`
  width: 33%;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const ParamsBlockRight = styled(ParamsBlock)`
  width: 66%;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const ParamsBlockTitle = styled.span`
  text-align: left;
  font-size: 17px;
  font-weight: 600;
  color: ${colors.thirdGray};
`

const StyledSelect = styled(Select)`
  width: 100%;
`

const ParamsBlocksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: 100%;

  @media ${device.tabletMax} {
    flex-direction: column;
  }

  div {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 25px;
  }
`

const TopBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 20px;

  @media ${device.tabletMax} {
    flex-direction: column;
  }
`

const BottomBlock = styled.div`
  width: calc(67% - 7px);
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const InfoInputBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  width: 100%;

  @media ${device.tabletMax} {
    flex-direction: column;
  }
`

const TogglesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`

interface MainInfoFormProps {
  clients?: Client[]
  control: Control<OfferFormData>
}

export const MainInfoForm: FC<MainInfoFormProps> = ({ clients, control }) => {
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false)

  const [addClient] = useAddClientMutation()

  const onAddClient = (client: AddClientBody): void => {
    addClient(client).then(() => {
      closeAddModal()
      notifications.show({
        title: 'Клиент успешно добавлен',
        message: '',
        position: 'top-right',
      })
    })
  }

  const formattedClients = (() => {
    if (!clients)
      return [
        {
          value: 'add_client',
          label: '+ Добавить клиента',
        },
      ]

    // Create a Map to ensure uniqueness based on client ID
    const uniqueClientsMap = new Map()

    clients.forEach((client) => {
      uniqueClientsMap.set(client.id, {
        value: client.id.toString(),
        label: formatClientName(client),
      })
    })

    return [
      ...Array.from(uniqueClientsMap.values()),
      {
        value: 'add_client',
        label: '+ Добавить клиента',
      },
    ]
  })()

  const handleClientChange = (
    value: string | null,
    originalOnChange: (value: string | null) => void,
  ): void => {
    if (value === 'add_client') {
      openAddModal()
      return
    }
    originalOnChange(value)
  }

  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const splittedSearch = search.toLowerCase().trim().split(' ')

    const regularOptions = (options as ComboboxItem[]).filter((option) => {
      if (option.value === 'add_client') {
        return false
      }

      const words = option.label.toLowerCase().trim().split(' ')
      return splittedSearch.every((searchWord) =>
        words.some((word) => word.includes(searchWord)),
      )
    })

    if (regularOptions.length === 0) {
      const addClientOption = (options as ComboboxItem[]).find(
        (option) => option.value === 'add_client',
      )
      return addClientOption ? [addClientOption] : []
    }

    return regularOptions
  }

  return (
    <>
      <AddClientModal
        onClose={closeAddModal}
        onAddClient={onAddClient}
        isOpened={isAddModalOpened}
      />
      <CalcParamsWrapper>
        <TopBlock>
          <ParamsBlockLeft>
            <ParamsBlockTitle>Информация о клиенте</ParamsBlockTitle>

            <Controller
              name='clientId'
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <StyledSelect
                  placeholder='Выберите клиента'
                  label={'Клиент'}
                  data={formattedClients}
                  value={value}
                  onChange={(newValue) =>
                    handleClientChange(newValue, onChange)
                  }
                  searchable
                  filter={optionsFilter}
                  error={error?.message}
                />
              )}
            />

            <Controller
              name='clientContactName'
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  placeholder={'ФИО в дательном падеже'}
                  value={value}
                  onChange={onChange}
                  label={'ФИО ответственного лица'}
                  error={error?.message}
                />
              )}
            />

            <Controller
              name='clientContactNamePosition'
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  placeholder={
                    'Должность ответственного лица в дательном падеже'
                  }
                  value={value}
                  onChange={onChange}
                  label={'Должность ответственного лица'}
                  error={error?.message}
                />
              )}
            />

            <Controller
              name='clientDivision'
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  placeholder={'Пример: Чульманская ТЭС'}
                  value={value}
                  onChange={onChange}
                  label={'Объект работ (Им. падеж)'}
                  error={error?.message}
                />
              )}
            />
          </ParamsBlockLeft>

          <ParamsBlockRight>
            <ParamsBlockTitle>
              Информация о расходах на объекте
            </ParamsBlockTitle>

            <ParamsBlocksWrapper>
              <div>
                <Controller
                  name='fuelPrice'
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      placeholder={'Цена ГСМ на объекте'}
                      value={value}
                      onChange={onChange}
                      type='money'
                      label={'Стоимость ГСМ на объекте с НДС, руб/л'}
                      error={error?.message}
                    />
                  )}
                />

                <Controller
                  name='partDeliveryPrice'
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      placeholder={
                        'Стоимость доставки запчастей и расходных материалов'
                      }
                      label={
                        'Стоимость доставки запчастей и расходных материалов, руб/мес'
                      }
                      value={value}
                      onChange={onChange}
                      type='money'
                      error={error?.message}
                    />
                  )}
                />

                <Controller
                  name='personDeliveryPrice'
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      placeholder={'Стоимость доставки персонала'}
                      label={'Стоимость доставки персонала, руб/мес'}
                      value={value}
                      onChange={onChange}
                      type='money'
                      error={error?.message}
                    />
                  )}
                />

                <Controller
                  name='dailyMedCheckCost'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      placeholder={'Стоимость ежедневного мед. осмотра'}
                      label={'Стоимость ежедневного медосмотра, руб/сут'}
                      value={value}
                      onChange={onChange}
                      type='money'
                    />
                  )}
                />
              </div>

              <div>
                <Controller
                  name='accommodationPrice'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      placeholder={'Стоимость проживания'}
                      label={'Стоимость проживания, руб/сут'}
                      value={value}
                      onChange={onChange}
                      type='money'
                    />
                  )}
                />

                <Controller
                  name='foodPrice'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      placeholder={'Стоимость питания'}
                      label={'Стоимость питания, руб/сут'}
                      value={value}
                      onChange={onChange}
                      type='money'
                    />
                  )}
                />

                <Controller
                  name='personnelHouseholdServicesCost'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      placeholder={'Стоимость бытового обслуживания'}
                      label={'Стоимость бытового обслуживания, руб/сут'}
                      value={value}
                      onChange={onChange}
                      type='money'
                    />
                  )}
                />
              </div>
            </ParamsBlocksWrapper>
          </ParamsBlockRight>
        </TopBlock>

        <BottomBlock>
          <ParamsBlockTitle>Информация о КП</ParamsBlockTitle>
          <InfoInputBlock>
            <Controller
              name='createDate'
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  placeholder={'Дата подготовки'}
                  value={value}
                  onChange={onChange}
                  label={'Дата подготовки'}
                />
              )}
            />

            <Controller
              name='season'
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label={'Сезон'}
                  onChange={onChange}
                  value={value}
                  data={[
                    {
                      label: 'Лето',
                      value: 'summer',
                    },
                    {
                      label: 'Зима',
                      value: 'winter',
                    },
                  ]}
                  defaultValue={getSeasons()}
                />
              )}
            />

            <Controller
              name='validityDaysCount'
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  placeholder={'Срок действия, дн.'}
                  label={'Срок действия, дн'}
                  value={value}
                  onChange={onChange}
                  type='number'
                  error={error?.message}
                />
              )}
            />

            <Controller
              name='minRentPeriod'
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  placeholder={'Минимальный срок аренды, мес.'}
                  label={'Мин. срок аренды'}
                  value={value}
                  onChange={onChange}
                  type='number'
                />
              )}
            />
          </InfoInputBlock>
        </BottomBlock>

        <BottomBlock>
          <HintTooltip label='Что попадёт в КП: блок персональных привилегий и какие цены показывать — с НДС, без НДС или обе'>
            <ParamsBlockTitle>Содержание КП</ParamsBlockTitle>
          </HintTooltip>
          <TogglesRow>
            <Controller
              name='personalBenefits'
              control={control}
              render={({ field: { onChange, value } }) => (
                <ToggleSwitch
                  checked={Boolean(value)}
                  onChange={(event) => onChange(event.currentTarget.checked)}
                  label='Персональные привилегии'
                  data-testid='personal-benefits-switch'
                />
              )}
            />

            <Controller
              name='vatVisibility'
              control={control}
              render={({ field: { onChange, value } }) => {
                const switches = toVatSwitches(value)
                const onVatSwitchChange = (next: VatSwitches): void => {
                  const nextValue = fromVatSwitches(next)
                  if (nextValue) onChange(nextValue)
                }

                return (
                  <>
                    <ToggleSwitch
                      checked={switches.withVat}
                      onChange={(event) =>
                        onVatSwitchChange({
                          ...switches,
                          withVat: event.currentTarget.checked,
                        })
                      }
                      label='С НДС'
                      data-testid='vat-switch'
                    />
                    <ToggleSwitch
                      checked={switches.withoutVat}
                      onChange={(event) =>
                        onVatSwitchChange({
                          ...switches,
                          withoutVat: event.currentTarget.checked,
                        })
                      }
                      label='Без НДС'
                      data-testid='no-vat-switch'
                    />
                  </>
                )
              }}
            />
          </TogglesRow>
        </BottomBlock>
      </CalcParamsWrapper>
    </>
  )
}
